/**
 * Safe, user-facing messages from Firebase Callable / network errors for Blacka chat.
 * Strips stack traces, tokens, and other sensitive fragments.
 */

const SENSITIVE_PATTERNS = [
  /Bearer\s+\S+/gi,
  /ya29\.[a-zA-Z0-9._-]+/g,
  /AIza[0-9A-Za-z_-]{35}/g,
  /sk-[a-zA-Z0-9]+/g,
  /-----BEGIN[\s\S]*?-----END[^-]*-----/g,
];

const MAX_MESSAGE_LENGTH = 280;

function stripSensitive(text: string): string {
  let result = text;
  for (const pattern of SENSITIVE_PATTERNS) {
    result = result.replace(pattern, '[redacted]');
  }
  const stackIndex = result.indexOf('\n    at ');
  if (stackIndex !== -1) {
    result = result.slice(0, stackIndex);
  }
  return result.trim().slice(0, MAX_MESSAGE_LENGTH);
}

function callableMessageFromCode(code: string): string {
  switch (code) {
    case 'functions/failed-precondition':
      return 'Blacka chatbot is currently disabled.';
    case 'functions/resource-exhausted':
      return 'Too many messages — give Blacka a likkle breather and try again soon.';
    case 'functions/unavailable':
      return 'Blacka is temporarily unavailable. Try again shortly.';
    case 'functions/unauthenticated':
      return 'You need to sign in to use Blacka.';
    case 'functions/internal':
      return 'Blacka could not respond right now. Try again in a minute.';
    default:
      return '';
  }
}

function extractCallableMessage(err: { code?: string; message?: string }): string {
  const raw = typeof err.message === 'string' ? err.message.trim() : '';
  if (raw) {
    const withoutPrefix = raw
      .replace(/^Firebase:\s*/i, '')
      .replace(/^Error:\s*/i, '')
      .replace(/^INTERNAL\s*/i, '')
      .trim();
    if (withoutPrefix) {
      return withoutPrefix;
    }
  }

  if (typeof err.code === 'string' && err.code.startsWith('functions/')) {
    return callableMessageFromCode(err.code);
  }

  return '';
}

/**
 * Returns a short message safe to show in the chat UI (no secrets or stack traces).
 */
export function getSafeBlackaChatErrorMessage(err: unknown): string {
  if (err && typeof err === 'object') {
    const cleaned = stripSensitive(extractCallableMessage(err as { code?: string; message?: string }));
    if (cleaned) {
      return cleaned;
    }
  }

  return 'Something went wrong. Try again in a minute.';
}

/**
 * Assistant bubble text when a send fails (Creole tone + surfaced server detail).
 */
export function formatBlackaChatErrorReply(detail: string): string {
  const trimmed = detail.trim();
  if (!trimmed) {
    return 'Sorry bredren, mi cyaan reach di server right now. Check yuh connection or email packmycart.help@gmail.com fi help.';
  }

  if (
    trimmed.includes('likkle breather') ||
    trimmed.includes('currently disabled') ||
    trimmed.includes('not configured yet')
  ) {
    return trimmed;
  }

  return `Sorry bredren — ${trimmed} Yuh can email packmycart.help@gmail.com fi more help.`;
}
